import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { ArrowLeft, RotateCcw, ArrowRight } from "lucide-react";
import {
  QUIZ_QUESTIONS,
  computeResult,
  URGENCY_NOTE,
} from "../data/quizData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Lightweight analytics helper (PostHog is present on the site)
const track = (event, props = {}) => {
  try {
    if (typeof window !== "undefined" && window.posthog) {
      window.posthog.capture(event, props);
    }
  } catch (e) {
    /* no-op */
  }
};

const Quiz = () => {
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [finished, setFinished] = useState(false);
  const [started, setStarted] = useState(false);
  const submittedRef = useRef(false);

  const total = QUIZ_QUESTIONS.length;
  const q = QUIZ_QUESTIONS[current];

  const result = finished ? computeResult(answers) : null;

  useEffect(() => {
    if (finished && result && !submittedRef.current) {
      submittedRef.current = true;
      track("quiz_result_shown", {
        rule: result.rule,
        score: result.score,
        perc: result.perc,
        urgenza: result.urgenza,
      });
      // Persist response for the back office analytics
      axios
        .post(`${API}/quiz/submit`, { answers })
        .catch((e) => console.error("quiz submit failed", e));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished]);

  const handleSelect = (opt) => {
    if (!started) {
      track("quiz_started");
      setStarted(true);
    }
    const next = { ...answers, [q.id]: opt.key };
    setAnswers(next);
    track("quiz_answer", { question: q.id, answer: opt.key });

    // avanzamento automatico (cambio pagina) al tap
    setTimeout(() => {
      if (current + 1 < total) {
        setCurrent((c) => c + 1);
      } else {
        setFinished(true);
      }
    }, 300);
  };

  const goBack = () => {
    if (current > 0) setCurrent((c) => c - 1);
  };

  const restart = () => {
    setAnswers({});
    setCurrent(0);
    setFinished(false);
    setStarted(false);
    submittedRef.current = false;
  };

  const perc = finished ? 100 : Math.round(((current + 1) / total) * 100);
  const urgencyNote = result ? URGENCY_NOTE[result.urgenza] : null;

  return (
    <section className="quiz-section" id="quiz">
      <div className="quiz-head">
        <p className="section-eyebrow">Fai il test</p>
        <h2 className="quiz-title serif">
          Qual è il potenziale della tua struttura?
        </h2>
        <p className="quiz-intro">
          Rispondi a {total} domande: ti indico il passo giusto per la tua
          struttura turistica.
        </p>
      </div>

      <div className="quiz-card">
        {/* Progress */}
        <div className="quiz-progress">
          <span className="quiz-step-label">
            {finished ? "Completato" : `Domanda ${current + 1} di ${total}`}
          </span>
          <span className="quiz-step-perc">{perc}%</span>
        </div>
        <div className="quiz-progress-bar">
          <div className="quiz-progress-fill" style={{ width: `${perc}%` }} />
        </div>

        {!finished ? (
          <div className="quiz-fade" key={current}>
            <h3 className="quiz-question serif">{q.question}</h3>
            <div className="quiz-options">
              {q.options.map((opt) => (
                <button
                  key={opt.key}
                  className={`quiz-option ${
                    answers[q.id] === opt.key ? "selected" : ""
                  }`}
                  onClick={() => handleSelect(opt)}
                >
                  <span className="opt-letter">{opt.key}</span>
                  <span className="opt-text">{opt.text}</span>
                </button>
              ))}
            </div>

            {current > 0 && (
              <button className="quiz-back" onClick={goBack}>
                <ArrowLeft size={16} strokeWidth={1.8} />
                Indietro
              </button>
            )}
          </div>
        ) : (
          <div className="quiz-fade quiz-result" key="result">
            <p className="quiz-result-eyebrow">Il tuo risultato</p>
            <h3 className="quiz-result-title serif">{result.title}</h3>
            {result.text.map((p, i) => (
              <p className="quiz-result-text" key={i}>
                {p}
              </p>
            ))}

            {urgencyNote && (
              <p className="quiz-urgency">{urgencyNote}</p>
            )}

            <div className="quiz-ctas">
              {result.ctas.map((cta) => (
                <a
                  key={cta.label}
                  href={cta.href}
                  className={`quiz-cta ${cta.primary ? "quiz-cta-primary" : "quiz-cta-ghost"}`}
                  onClick={() =>
                    track("quiz_cta_click", {
                      rule: result.rule,
                      label: cta.label,
                      href: cta.href,
                    })
                  }
                >
                  <span>{cta.label}</span>
                  <ArrowRight size={16} strokeWidth={1.8} />
                </a>
              ))}
            </div>

            <button className="quiz-back quiz-restart" onClick={restart}>
              <RotateCcw size={15} strokeWidth={1.8} />
              Rifai il test
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Quiz;
